import { useCreateDossier } from "@/entities/dossier";
import { Reikwijdte } from "@/entities/dossier-details";
import { useDeclineOngewensteGebeurtenis } from "@/entities/ongewenste-gebeurtenis";
import { useGetAvailableOngewensteGebeurtenissen } from "@/features/dossier-management/hooks";
import { Button, DialogFooter, ErrorDisplay, Form } from "@/shared/components";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import { NieuwDossierFormValues, buildCreateDossierInput, getDefaultValues, nieuwDossierSchema } from "../../utils";
import { NietSpoorwebStep } from "./niet-spoorweb-step";
import { SecondaryActionButton } from "./secondary-action-button";
import { SelectStep } from "./select-step";
import { SpoorwebStep } from "./spoorweb-step";

type Step = "select" | "spoorweb" | "niet-spoorweb";

type NewDossierFormProps = {
    onOpenChange: (open: boolean) => void;
    preselectedOgNummer?: string;
};

export function NewDossierForm({ onOpenChange, preselectedOgNummer }: Readonly<NewDossierFormProps>) {
    const createDossier = useCreateDossier();
    const { mutate: declineSpoorwebDossier } = useDeclineOngewensteGebeurtenis();
    const { ongewensteGebeurtenissen, options: ogOptions } = useGetAvailableOngewensteGebeurtenissen();
    // Default reikwijdte for non-spoorweb dossiers is Stations (if FeatureFlag is enabled), otherwise it's Trajecten
    const defaultReikwijdte = Reikwijdte.Stations;
    const navigate = useNavigate();
    const [submitError, setSubmitError] = useState<Error | null>(null);

    const [step, setStep] = useState<Step>(preselectedOgNummer ? "spoorweb" : "select");

    const getOngewensteGebeurtenis = useCallback(
        (spoorwebNr: string) => ongewensteGebeurtenissen.find((og) => og.nummer === spoorwebNr),
        [ongewensteGebeurtenissen]
    );

    const initialTrajecten = preselectedOgNummer ? (getOngewensteGebeurtenis(preselectedOgNummer)?.trajecten ?? []) : [];

    const form = useForm<NieuwDossierFormValues>({
        resolver: zodResolver(nieuwDossierSchema),
        defaultValues: getDefaultValues(defaultReikwijdte, preselectedOgNummer, initialTrajecten),
    });

    const currentSpoorwebNr = form.watch("spoorwebNr");
    const currentSpoorwebItem = getOngewensteGebeurtenis(currentSpoorwebNr);

    const handleSpoorwebNrSelect = (spoorwebNr: string) => {
        const og = getOngewensteGebeurtenis(spoorwebNr);
        form.reset(getDefaultValues(Reikwijdte.Trajecten, spoorwebNr, og?.trajecten ?? []));
        setStep("spoorweb");
    };

    const handleZonderSpoorweb = () => {
        form.reset(getDefaultValues(defaultReikwijdte));
        setStep("niet-spoorweb");
    };

    const handleBack = () => {
        form.reset(getDefaultValues(defaultReikwijdte));
        setStep("select");
    };

    const handleWeigeren = () => {
        if (preselectedOgNummer) {
            declineSpoorwebDossier(preselectedOgNummer);
        }
        onOpenChange(false);
    };

    const onSubmit = async (values: NieuwDossierFormValues) => {
        try {
            setSubmitError(null);
            const dossierNummer = await createDossier.mutateAsync(buildCreateDossierInput(values));
            onOpenChange(false);
            void navigate(`/dossier/${dossierNummer}`);
        } catch (error) {
            setSubmitError(error as Error);
        }
    };

    const backButtonLabel = useMemo(() => {
        if (step === "select") return null;
        if (step === "spoorweb" && preselectedOgNummer) return "Weigeren";
        return "Vorige stap";
    }, [step, preselectedOgNummer]);

    useEffect(() => {
        form.setFocus(preselectedOgNummer ? "omschrijving" : "spoorwebNr");
    }, [preselectedOgNummer, form]);

    return (
        <Form {...form}>
            <form onSubmit={(e) => void form.handleSubmit(onSubmit)(e)}>
                {step === "select" && (
                    <SelectStep
                        form={form}
                        options={ogOptions}
                        onSpoorwebSelect={handleSpoorwebNrSelect}
                        continueWithoutOg={handleZonderSpoorweb}
                    />
                )}

                {step === "spoorweb" && <SpoorwebStep currentSpoorwebItem={currentSpoorwebItem} />}

                {step === "niet-spoorweb" && <NietSpoorwebStep />}

                {step !== "select" && (
                    <DialogFooter>
                        <div className="flex w-full items-center justify-between gap-2">
                            {backButtonLabel && (
                                <SecondaryActionButton
                                    action={preselectedOgNummer ? "decline" : "previous"}
                                    onClick={preselectedOgNummer ? handleWeigeren : handleBack}
                                >
                                    {backButtonLabel}
                                </SecondaryActionButton>
                            )}
                            <Button
                                type="submit"
                                disabled={form.formState.isSubmitting || currentSpoorwebItem?.meldingStatus === "Gesloten"}
                            >
                                Dossier aanmaken
                            </Button>
                        </div>
                        {submitError && (
                            <div className="p-2">
                                <ErrorDisplay error={submitError} title="Fout bij het aanmaken van dossier" />
                            </div>
                        )}
                    </DialogFooter>
                )}
            </form>
        </Form>
    );
}
